import React, { useState } from "react";
import { ReactComponent as InformationIcon } from "./../../images/svg/information.svg";
import { ReactComponent as PinIcon } from "./../../images/svg/pin.svg";
import { ReactComponent as CalendarIcon } from "./../../images/svg/calendar.svg";
import { gettext, cropr } from "../../adapter";
import map from "lodash/map";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import contains from "lodash/includes";
import Map from "./Map";
import DepthChart from "./DepthChart";
import { useDispatch } from "react-redux";
import sensorActions from "../../actions/sensorActions";
import { currentDatePickerLocale } from "../../libraries/datepickerLocales";

const InformationEdit = ({ sensor, lastDataPoint, stateText, domain }) => {
  const [helpOpen, setHelpOpen] = useState(false);
  const dispatch = useDispatch();
  const soilTypes = {
    "01": { name: "Sand", maxSoilMoisture: 6 },
    "02": { name: "Sandy loam", maxSoilMoisture: 12 },
    "03": { name: "Loamy sand", maxSoilMoisture: 14 },
    "04": { name: "Silt", maxSoilMoisture: 13 },
    "06": { name: "Loam", maxSoilMoisture: 7 },
    "07": { name: "Sandy clay loam ", maxSoilMoisture: 10 },
    "09": { name: "Clay", maxSoilMoisture: 14 },
    12: { name: "Peat", maxSoilMoisture: 19 },
  };
  var soilType = "";
  var precision = cropr.l10n.unit_precipitation === "in" ? 1 : 0;
  var groundOptions = map(soilTypes, function (values, key) {
    var checked = sensor.soilType === key ? true : false;
    return (
      <li key={key}>
        <label htmlFor={key}>
          <input
            type="radio"
            data-capacity={values.maxSoilMoisture}
            id={key}
            name="soilType"
            value={key}
            onChange={(event) =>
              dispatch(
                sensorActions.updateSoilType(event.target.value, event.target.dataset.capacity)
              )
            }
            defaultChecked={checked}
          />
          {gettext(values.name)}
          <small>
            ({cropr.toLocaleFixed(cropr.l10n_utils.mm_to_l10n(values.maxSoilMoisture), precision)}{" "}
            {cropr.l10n.unit_precipitation_display})
          </small>
        </label>
      </li>
    );
  });

  soilType = (
    <div>
      <div className="has-label-styles">{gettext("Soil type")}</div>
      <div className="message">
        {gettext(
          "Changing the soil type will reset the Maximum Available Water to standard values."
        )}
      </div>
      <ul className="form--checklist has-grid">{groundOptions}</ul>
    </div>
  );

  // TurnedOn state only for senstion
  var enabled = "";
  if (sensor.role === "owner" && sensor.enabled === true) {
    if (sensor.turned_off) {
      enabled = (
        <div className="text-center">
          <strong>
            {gettext(
              'Sensor will be turned off at the next communication. Confirm with "SAVE CHANGES"'
            )}
          </strong>
        </div>
      );
    } else {
      enabled = (
        <li>
          <label>
            Status <small>{stateText}</small>
          </label>
          <div className="buttons">
            {/* eslint-disable-next-line */}
            <a
              className="button is-left"
              onClick={() => {
                if (window.confirm(gettext("Are you sure?"))) {
                  dispatch(sensorActions.updateTurnedOff(true));
                }
              }}
            >
              {gettext("Switch {{type}} off").replace("{{type}}", sensor.type)}
            </a>
          </div>
        </li>
      );
    }
  }

  var depthOptions = map(sensor.depths, function (values, depth) {
    var checked = false;
    if (values.visible) {
      checked = true;
    }

    return (
      <li key={"depthOptions" + depth}>
        <label className="checkbox-label">
          <input
            type="checkbox"
            data-depth={depth}
            id={"depth-" + depth}
            name={"depth-" + depth}
            defaultChecked={checked}
            onChange={(event) => {
              dispatch(
                sensorActions.updateDepthVisibility(
                  event.target.dataset.depth,
                  event.target.checked
                )
              );
            }}
          />
          <span className="checkbox"></span>
          {gettext("{{depth}} {{unit}} depth")
            .replace("{{depth}}", cropr.l10n_utils.depth_to_l10n(depth))
            .replace("{{unit}}", cropr.l10n.unit_depth_display)}
        </label>
      </li>
    );
  });

  // Only show settings if they're relevant to the sensor
  var showViewSettings = "";

  if (sensor.type.toLowerCase().indexOf("terrasen") >= 0) {
    var showPrecipitation = "";
    var showTemperature = "";

    if (contains(sensor.sensors, "precipitation")) {
      showPrecipitation = (
        <li>
          <label className="checkbox-label">
            <input
              type="checkbox"
              id="showPrecipitation"
              name="showPrecipitation"
              defaultChecked={sensor.showPrecipitation}
              onChange={(event) =>
                dispatch(sensorActions.updateShowPrecipitation(event.target.checked))
              }
            />
            <span className="checkbox"></span>
            {gettext("Show precipitation graph")}
          </label>
        </li>
      );
    }

    if (sensor.measurements.temperature) {
      showTemperature = (
        <li>
          <label className="checkbox-label">
            <input
              type="checkbox"
              id="showTemperature"
              name="showTemperature"
              defaultChecked={sensor.showTemperature}
              onChange={(event) =>
                dispatch(sensorActions.updateShowTemperature(event.target.checked))
              }
            />
            <span className="checkbox"></span>
            {gettext("Show temperature graphs")}
          </label>
        </li>
      );
    }

    showViewSettings = (
      <li>
        <label>{gettext("View settings")}</label>
        <div className="form--fields">
          <ul className="form--checklist">
            {showPrecipitation}
            {showTemperature}
          </ul>
        </div>
      </li>
    );
  }

  var showLegend = true;
  var depthSettings = map(sensor.depths, function (values, depth) {
    var chart = (
      <DepthChart
        key={"depthSettings" + depth}
        sensor={sensor}
        depth={depth}
        dispatch={dispatch}
        domain={domain}
        lastDataPoint={lastDataPoint}
        showLegend={showLegend}
      />
    );
    showLegend = false;
    return chart;
  });

  var errors = sensor.errors;

  var helpClass = "panel-options__item dropdown__wrapper";
  if (helpOpen) {
    helpClass = "panel-options__item dropdown__wrapper is-open";
  }

  var sensorEditHelpLink =
    "https://www.dacom.nl/dacom_online_help/sensorEdit_" + cropr.l10n.languageCode + ".html";

  return (
    <div>
      <div className="panel--header">
        <h2>{gettext("Settings of this {{sensor}}").replace("{{sensor}}", sensor.type)}</h2>
        <ul className="panel-options">
          <li
            className={helpClass}
            onClick={() => {
              setHelpOpen(!helpOpen);
            }}
          >
            {/* eslint-disable-next-line */}
            <a className="dropdown--toggle">
              <InformationIcon className="icon" />
            </a>

            <div className="dropdown dropdown--wide">
              <div className="dropdown__content">
                <iframe title="sensorEditHelpLink" src={sensorEditHelpLink}></iframe>
              </div>
            </div>
          </li>
        </ul>

        <div className="panel--settings">
          <div className="panel--settings__item">
            <div
              id="information"
              className="dropdown"
              onClick={() => {
                var info = document.querySelector("#information"); // Using a class instead, see note below.
                info.classList.toggle("is-open");
              }}
            >
              {/* eslint-disable-next-line */}
              <a className="panel--settings__icon">
                <InformationIcon className="icon" />
              </a>
              <div className="dropdown--popout is-visible dropdown--popout__moretop dropdown--popout__wide">
                <div className="dropdown--popout__content">
                  <p>Ik ben de HTML content</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ul className="form has-no-spacing">
        <li className={errors.name === "required" ? "form--error" : ""}>
          <label>{gettext("Name")}</label>
          <div className="form--fields">
            <input
              type="text"
              id="name"
              name="name"
              defaultValue={sensor.name}
              onChange={(event) => {
                dispatch(sensorActions.updateName(event.target.value));
                var id = sensor.id;
                var sensorscache = JSON.parse(window.localStorage.getItem("sensorsDDCache"));
                if (sensorscache && sensorscache.length > 0) {
                  for (var i = 0; i < sensorscache.length; i++) {
                    if (sensorscache[i].id === id) {
                      sensorscache[i].name = event.target.value;
                      window.localStorage.setItem("sensorsDDCache", JSON.stringify(sensorscache));
                    }
                  }
                }
              }}
            />
            {errors.name === "required" ? (
              <ul className="form--errors">
                <li className="error--message">{gettext("Name is required.")}</li>
              </ul>
            ) : (
              ""
            )}
          </div>
        </li>

        {enabled}

        <li>
          {/* https://hacker0x01.github.io/react-datepicker/ on blur callback */}
          <label>{gettext("Measurement period")}</label>
          <div>
            <p className="has-medium-spacing">
              {gettext("The period between installation and removal from a specific field.")}
            </p>
          </div>
          <div className="form--combined">
            <div
              className={
                errors.startDate ? "form--combined-field form--error" : "form--combined-field"
              }
            >
              <label className="has-normal-weight">{gettext("Start date")}</label>
              <div className="form--fields has-icon-within-field">
                <CalendarIcon className="icon" style={{ zIndex: 100 }} />
                <DatePicker
                  name="startDate"
                  dateFormat="dd-MM-yyyy"
                  locale={currentDatePickerLocale()}
                  selected={sensor.startDate ? sensor.startDate.toDate() : null}
                  maxDate={sensor.endDate ? sensor.endDate.toDate() : null}
                  onChange={(value) =>
                    dispatch(
                      sensorActions.updateStartDate(value instanceof Date ? moment(value) : "")
                    )
                  }
                />
                {errors.startDate === "required" ? (
                  <ul className="form--errors">
                    <li className="error--message">{gettext("Start date is required.")}</li>
                  </ul>
                ) : (
                  ""
                )}
              </div>
            </div>
            <div
              className={
                errors.endDate ? "form--combined-field form--error" : "form--combined-field"
              }
            >
              <label className="has-normal-weight">{gettext("End date")}</label>
              <div className="form--fields has-icon-within-field">
                <CalendarIcon className="icon" style={{ zIndex: 100 }} />
                <DatePicker
                  name="endDate"
                  dateFormat="dd-MM-yyyy"
                  locale={currentDatePickerLocale()}
                  selected={sensor.endDate ? sensor.endDate.toDate() : null}
                  minDate={sensor.startDate ? sensor.startDate.toDate() : null}
                  onChange={(value) =>
                    dispatch(
                      sensorActions.updateEndDate(value instanceof Date ? moment(value) : "")
                    )
                  }
                />
                {errors.endDate === "required" ? (
                  <ul className="form--errors">
                    <li className="error--message">{gettext("End date is required.")}</li>
                  </ul>
                ) : (
                  ""
                )}
              </div>
            </div>
          </div>
        </li>
        {showViewSettings}
        <li>
          <label>{gettext("Position")}</label>
          <div>
            <p className="has-medium-spacing">
              {gettext(
                "Enter the latitude and longitude of the sensor or drag the sensor to the required position on the map."
              )}
            </p>
          </div>
          <div className="form--combined">
            <div
              className={
                errors.latitude ? "form--combined-field form--error" : "form--combined-field"
              }
            >
              <label className="has-normal-weight">{gettext("Latitude")}</label>
              <div className="form--fields has-icon-within-field">
                <PinIcon className="icon" />
                <input
                  type="text"
                  id="latitude"
                  name="latitude"
                  value={sensor.latitude}
                  onChange={(event) => {
                    dispatch(sensorActions.updateLatitude(event.target.value));
                  }}
                />
                {errors.latitude === "invalid" ? (
                  <ul className="form--errors">
                    <li className="error--message">{gettext("Enter a valid latitude.")}</li>
                  </ul>
                ) : (
                  ""
                )}
                {errors.latitude === "required" ? (
                  <ul className="form--errors">
                    <li className="error--message">{gettext("Position is required.")}</li>
                  </ul>
                ) : (
                  ""
                )}
              </div>
            </div>
            <div
              className={
                errors.longitude ? "form--combined-field form--error" : "form--combined-field"
              }
            >
              <label className="has-normal-weight">{gettext("Longitude")}</label>
              <div className="form--fields has-icon-within-field ">
                <PinIcon className="icon" />
                <input
                  type="text"
                  id="longitude"
                  name="longitude"
                  value={sensor.longitude}
                  onChange={(event) => {
                    dispatch(sensorActions.updateLongitude(event.target.value));
                  }}
                />
                {errors.longitude === "invalid" ? (
                  <ul className="form--errors">
                    <li className="error--message">{gettext("Enter a valid longitude.")}</li>
                  </ul>
                ) : (
                  ""
                )}
                {errors.longitude === "required" ? (
                  <ul className="form--errors">
                    <li className="error--message">{gettext("Position is required.")}</li>
                  </ul>
                ) : (
                  ""
                )}
              </div>
            </div>
          </div>
        </li>
      </ul>

      <Map className="map small" sensor={sensor} draggable={true} />

      <div className="panel--spacer">
        {sensor.type === "Sensetion" ? (
          <h2>{gettext("Soil and depth settings")}</h2>
        ) : (
          <h2>{gettext("Depth settings")}</h2>
        )}
      </div>
      {soilType}

      <div className="has-label-styles">{gettext("Activated sensor depth")}</div>
      <div>
        <p className="has-medium-spacing">
          {gettext(
            "The selected depths are shown in the graph and used to sum the Maximum Available Water in the selected depth and are used for information in the mobile app."
          )}
        </p>
      </div>
      <div className="message">
        {gettext("The sum of the Maximum Available Water in the selected depth: {{sum}} {{unit}}")
          .replace("{{sum}}", cropr.l10n_utils.mm_to_l10n(sensor.fieldCapacity))
          .replace("{{unit}}", cropr.l10n.unit_precipitation_display)}
      </div>

      {errors.depths === "required" ? (
        <ul className="form--errors">
          <li className="error--message">{gettext("Select at least one depth.")}</li>
        </ul>
      ) : (
        ""
      )}
      <ul className="form--checklist has-grid">{depthOptions}</ul>

      <div
        className={
          errors.warningPercentage === "invalid" ? "form--error has-big-spacing" : "has-big-spacing"
        }
      >
        <label htmlFor="warningPercentage">
          {gettext("Warn at low soil moisture level (percentage)")}
        </label>
        <div>
          <p className="has-medium-spacing">
            {gettext(
              "You'll be warned if the cumulative soil moisture drops below this percentage."
            )}
          </p>
        </div>
        <div className="form--fields">
          <input
            type="number"
            id="warningPercentage"
            name="warningPercentage"
            defaultValue={sensor.warningPercentage}
            onChange={(event) =>
              dispatch(sensorActions.updateWarningPercentage(event.target.value))
            }
          />
          {errors.warningPercentage === "invalid" ? (
            <ul className="form--errors">
              <li className="error--message">
                {gettext(
                  "Enter a percentage between 1 and 99 or leave this field empty to discard the warning message."
                )}
              </li>
            </ul>
          ) : (
            ""
          )}
        </div>
      </div>

      <div>
        <div className="has-label-styles">{gettext("Edit warning points")}</div>
        <div>
          <p>
            {gettext(
              "For each depth the Field Capacity and the Refill Point can be adjusted by moving the lines. Per depth the Maximum Available Water can be changed from the default value of the chosen soil type."
            )}
          </p>
        </div>

        <ul className="chart-depth--list">{depthSettings}</ul>
      </div>
    </div>
  );
};

export default InformationEdit;
